import React, { useEffect, useState } from "react";
import { Loader2, Search, User2, Database, WifiOff, RotateCw, ArrowRight } from "lucide-react";
import { reApi, errMsg } from "@/lib/residentApi";
import ResidentDetail from "@/components/nds/windows/resident/ResidentDetail";
import { ArchiveViewChips, countViews, inView, normView } from "@/components/nds/ArchiveView";
import { BulkArchiveBar } from "@/components/nds/BulkArchiveBar";
import { StartSessionButton } from "@/components/nds/StartSessionButton";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { TestAccountsBar, TestBadge } from "@/components/nds/windows/resident/TestAccounts";
import { TeaPhoneTag } from "@/components/console/PreviewAs";

const fullName = (r) => `${r.first_name} ${r.last_name}`;
const byName = (a, b) => fullName(a).localeCompare(fullName(b));
const byIntake = (a, b) => String(a.intake_date || "").localeCompare(String(b.intake_date || ""));
const SORTS = {
  name_asc: { label: "Name A–Z", fn: byName },
  name_desc: { label: "Name Z–A", fn: (a, b) => byName(b, a) },
  intake_new: { label: "Newest intake", fn: (a, b) => byIntake(b, a) },
  intake_old: { label: "Oldest intake", fn: byIntake },
  phase: { label: "Phase", fn: (a, b) => String(a.current_phase).localeCompare(String(b.current_phase)) || byName(a, b) },
};

function usePersisted(key, initial) {
  const [val, setVal] = useState(() => {
    try { return localStorage.getItem(key) || initial; } catch { return initial; }
  });
  const set = (v) => { setVal(v); try { localStorage.setItem(key, v); } catch { /* storage unavailable */ } };
  return [val, set];
}

export function OfflineNotice({ error, onRetry }) {
  return (
    <div data-testid="campus-residents-error" className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-sm text-center">
        <div className="mx-auto h-12 w-12 rounded-full grid place-items-center bg-red-500/10 border border-red-400/40 mb-4">
          <WifiOff className="h-5 w-5 text-red-300" />
        </div>
        <p data-testid="campus-residents-error-message" className="text-nex-silver/80 text-sm leading-relaxed">{error.message}</p>
        <button type="button" onClick={onRetry} data-testid="campus-residents-retry"
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 border border-nex-gold/50 hover:bg-nex-gold/10 text-nex-gold font-mono text-[10px] tracking-[0.22em] uppercase rounded-sm transition-colors">
          <RotateCw className="h-3.5 w-3.5" /> Retry
        </button>
      </div>
    </div>
  );
}

function RosterControls({ view, setView, sort, setSort, counts }) {
  return (
    <div data-testid="campus-residents-controls" className="px-5 py-2.5 border-b border-white/10 flex flex-wrap items-center gap-2">
      <ArchiveViewChips prefix="campus-residents" view={view} setView={setView} counts={counts} />
      <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="campus-residents-sort" aria-label="Sort residents"
        className="ml-auto bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver font-mono text-[11px] px-2 py-1 rounded-sm">
        {Object.entries(SORTS).map(([id, s]) => <option key={id} value={id}>{s.label}</option>)}
      </select>
    </div>
  );
}

function ResidentRow({ r, onOpen, checked, onCheck }) {
  return (
    <li className="flex items-center">
      {onCheck && <input type="checkbox" checked={checked} onChange={() => onCheck(r.id)} data-testid={`campus-resident-check-${r.id}`} aria-label="Select resident" className="ml-5" />}
      <button type="button" onClick={() => onOpen(r.id)} data-testid={`campus-resident-row-${r.id}`}
        className="flex-1 min-w-0 text-left px-5 py-3 flex items-center gap-4 hover:bg-nex-gold/5 transition-colors">
        <div className="h-9 w-9 rounded-full grid place-items-center bg-nex-gold/15 text-nex-gold"><User2 className="h-4 w-4" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-nex-silver font-medium truncate">{fullName(r)}{r.preferred_name ? ` “${r.preferred_name}”` : ""}{r.is_test && <TestBadge />}{r.tea_phone && <span className="ml-2"><TeaPhoneTag id={r.id} /></span>}</div>
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-nex-muted truncate">{r.housing_label || "No housing"}</div>
        </div>
        <span className="hidden sm:inline-flex px-2 py-0.5 rounded-full border border-white/15 font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">Phase {r.current_phase}</span>
        <span className={`hidden sm:inline-flex px-2 py-0.5 rounded-full border font-mono text-[9px] tracking-[0.2em] uppercase ${
          r.status === "active" ? "border-emerald-400/40 text-emerald-300" : r.status === "archived" ? "border-amber-400/40 text-amber-300" : "border-white/15 text-nex-muted"}`}>{r.status}</span>
        <ArrowRight className="h-3.5 w-3.5 text-nex-muted" />
      </button>
      {r.status === "active" && <StartSessionButton residentId={r.id} name={fullName(r)} className="mr-4" />}
    </li>
  );
}

export default function CampusResidentContent({ win }) {
  const focus = win?.payload?.focus;
  const [state, setState] = useState({ loading: true, rows: [], error: null });
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(focus?.residentId || null);
  const [rawView, setView] = usePersisted("tea_re_view", "active");
  const view = normView(rawView);
  const [sort, setSort] = usePersisted("tea_re_sort", "name_asc");
  const [sel, setSel] = useState([]);
  const [arch, setArch] = useState(null);
  const isSuper = useIsSuper();
  const [showTests, setShowTests] = useState(false);
  useEffect(() => { reApi.get("/residents/archive/reasons").then(setArch).catch(() => {}); }, []);
  useEffect(() => { setSel([]); }, [rawView, attempt]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  useEffect(() => {
    let alive = true;
    reApi.get("/residents", { status: "all", ...(showTests ? { include_tests: true } : {}) })
      .then((d) => alive && setState({ loading: false, rows: d.residents, error: null }))
      .catch((e) => alive && setState({ loading: false, rows: [], error: { message: errMsg(e) } }));
    return () => { alive = false; };
  }, [attempt, showTests]);

  if (selected) {
    return <ResidentDetail residentId={selected} focus={focus?.residentId === selected ? focus : null} onBack={() => { setSelected(null); setAttempt((n) => n + 1); }} />;
  }

  const counts = countViews(state.rows);
  const q = query.toLowerCase();
  const rows = state.rows
    .filter((r) => inView(r, view))
    .filter((r) => !q || `${fullName(r)} ${r.preferred_name || ""} ${r.housing_label || ""}`.toLowerCase().includes(q))
    .sort((SORTS[sort] || SORTS.name_asc).fn);

  return (
    <div data-testid="campus-residents-content" className="flex flex-col h-full" style={{ minHeight: "460px" }}>
      {isSuper && <TestAccountsBar show={showTests} setShow={setShowTests} onChanged={() => setAttempt((n) => n + 1)} />}
      <div className="px-5 pt-4 pb-3 border-b border-white/10 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted" />
          <input data-testid="campus-residents-search" type="search" placeholder="Search name or housing…" value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver font-mono text-sm rounded-sm" />
        </div>
        <div data-testid="campus-residents-source" className="flex items-center gap-1.5 px-2.5 py-1.5 border border-white/10 rounded-sm">
          <Database className="h-3 w-3 text-nex-gold" />
          <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-nex-muted">Resident Engine</span>
        </div>
        <span data-testid="campus-residents-count" className="font-mono text-[10px] tracking-[0.22em] uppercase text-nex-gold">{rows.length}</span>
      </div>
      {!state.error && !state.loading && <RosterControls view={view} setView={setView} sort={sort} setSort={setSort} counts={counts} />}
      {arch?.can_archive && !state.loading && rows.length > 0 && (
        <div className="px-5 py-2 border-b border-white/10 flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-nex-muted">
            <input type="checkbox" checked={rows.every((r) => sel.includes(r.id))} onChange={() => setSel(rows.every((r) => sel.includes(r.id)) ? [] : rows.map((r) => r.id))} data-testid="campus-residents-select-all" /> Select all
          </label>
          <BulkArchiveBar kind="residents" label="residents" selected={sel} selectedArchived={state.rows.filter((r) => r.status === "archived").map((r) => r.id)}
            reasons={arch.reasons} canDelete={isSuper} onDone={() => setAttempt((n) => n + 1)} testid="campus-residents-bulk" />
        </div>
      )}
      {state.loading ? (
        <div className="flex-1 flex items-center justify-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading residents…</div>
      ) : state.error ? (
        <OfflineNotice error={state.error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : !rows.length ? (
        <div data-testid="campus-residents-empty" className="flex-1 flex items-center justify-center text-nex-muted text-sm">No residents match.</div>
      ) : (
        <ul className="flex-1 overflow-y-auto overflow-x-hidden divide-y divide-white/5">
          {rows.map((r) => <ResidentRow key={r.id} r={r} onOpen={setSelected} checked={sel.includes(r.id)} onCheck={arch?.can_archive ? toggle : null} />)}
        </ul>
      )}
    </div>
  );
}
